/* eslint-disable @nx/enforce-module-boundaries */
import {
  DestinationDetails,
  DestinationNode,
  DestinationNodeType,
  DistanceMatrixResponseType,
  LocationCoords,
  NearByDestinationsInfoProps,
} from '../NearByDestinations.types';

export const parseCoordinates = (
  lat: string | number | undefined,
  lng: string | number | undefined
): LocationCoords => {
  const parsedLat = Number(lat ?? 0);
  const parsedLng = Number(lng ?? 0);
  const isValid = Number.isFinite(parsedLat) && Number.isFinite(parsedLng) && parsedLat !== 0 && parsedLng !== 0;
  return isValid ? { lat: parsedLat, lng: parsedLng } : { lat: 0, lng: 0 };
};

export const normalizeVariation = (variation: string | undefined): string => {
  return (variation ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
};

const isValidLocationPart = (value: unknown): value is string | number => {
  if (value === null || value === undefined) {
    return false;
  }

  if (typeof value === 'string') {
    return value.trim().length > 0;
  }

  return true;
};

export const buildLocationText = (...parts: unknown[]): string => {
  return parts
    .filter(isValidLocationPart)
    .map(part => String(part).trim())
    .join(', ');
};

const hasQueryParamValue = (value: unknown): value is string | number | boolean => {
  if (value === null || value === undefined) {
    return false;
  }

  if (typeof value === 'string') {
    return value.trim().length > 0;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value);
  }

  return typeof value === 'boolean';
};

export const buildDestinationListWithoutDistance = (
  destinations: Array<DestinationNodeType>
): NearByDestinationsInfoProps => {
  return {
    destinationList: destinations.map(dest => ({
      destination: buildLocationText(
        dest.participatingProperties.city,
        dest.participatingProperties.stateProvince,
        dest.participatingProperties.country
      ),
      distance: '',
      duration: 0,
      placeId: '',
      address: buildLocationText(dest.participatingProperties.city, dest.participatingProperties.country),
      city: dest.participatingProperties.city ?? '',
      country: dest.participatingProperties.countryCode ?? '',
      latitude: `${dest.participatingProperties.properties[0].basicInformation.latitude}`,
      longitude: `${dest.participatingProperties.properties[0].basicInformation.longitude}`,
      state: dest.participatingProperties.stateProvince,
      seconds: 0,
      hoursAwayText: '',
    })),
  };
};

export const resolveTitleWithDestinationName = (titleTemplate: string = '', destinationName: string = ''): string => {
  if (!titleTemplate || !destinationName) {
    return titleTemplate;
  }

  return titleTemplate.replace(/\{\s*destinationname\s*\}/gi, destinationName);
};

export const getGeoLocationCoordinates = (datalayer?: Record<string, string | number>): LocationCoords => {
  const windowDataLayer =
    typeof window !== 'undefined' ? (window as { dataLayer?: Record<string, string | number> }).dataLayer ?? {} : {};

  const windowCoords = parseCoordinates(
    windowDataLayer?.['browser_akamai_loc_lat'],
    windowDataLayer?.['browser_akamai_loc_long']
  );
  if (windowCoords.lat !== 0 && windowCoords.lng !== 0) {
    return windowCoords;
  }
  const datalayerCoords = parseCoordinates(
    datalayer?.['browser_akamai_loc_lat'],
    datalayer?.['browser_akamai_loc_long']
  );
  return datalayerCoords;
};

export const getNearByDestinationsVariables = (
  latitude: number,
  longitude: number,
  minDistance: string | number,
  maxDistance: string | number
) => {
  const obj = {
    input: {
      queries: [
        {
          id: 'latitude',
          values: [`${latitude}`],
        },
        {
          id: 'longitude',
          values: [`${longitude}`],
        },
      ],
      facets: {
        terms: [
          {
            type: 'DISTANCE_RANGE',
            dimensions: [minDistance, maxDistance],
          },
          {
            type: 'CITIES',
            dimensions: [],
          },
        ],
      },
    },
    sort: { field: 'OFFER_DISTANCE' },
  };
  return obj;
};

// getting destinations which has more offers than maxOffersPerCity
// and display cities equal to numCitiesToDisplay
export const filterDestinations = (
  edges: Array<DestinationNode>,
  minOffersPerCity: string | number,
  numCitiesToDisplay: string | number
): Array<DestinationNodeType> => {
  const destinations = new Map();
  const filteredEdges = edges.filter(item => item.node.participatingProperties.city);
  const edgeLength = filteredEdges.length;
  for (let i = 0; i < edgeLength; i++) {
    const city = filteredEdges[i].node.participatingProperties.city;
    if (!destinations.has(city)) {
      destinations.set(city, { nodes: [filteredEdges[i].node], offerCount: 1 });
    } else {
      const cityData = destinations.get(city);
      cityData.nodes.push(filteredEdges[i].node);
      cityData.offerCount += 1;
    }
    if (destinations.size > Number(numCitiesToDisplay)) {
      break;
    }
  }
  const destinationArray: Array<DestinationNodeType> = [];
  destinations.forEach(city => {
    if (city.offerCount >= minOffersPerCity && destinationArray.length < Number(numCitiesToDisplay)) {
      destinationArray.push(city.nodes[0]);
    }
  });
  return destinationArray;
};

export const getDistanceMatrixForDestinations = async (
  origins: LocationCoords,
  destinationsCoordinates: Array<LocationCoords>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<any> => {
  const service = new google.maps.DistanceMatrixService();

  const request = {
    origins: [origins],
    destinations: destinationsCoordinates,
    unitSystem: google.maps.UnitSystem.IMPERIAL,
    travelMode: google.maps.TravelMode.DRIVING,
  };

  try {
    const response = await service.getDistanceMatrix(request);
    return response;
  } catch (error) {
    console.error('getDistanceMatrixForDestinations Error occurred:', error);
    throw error; // optional, rethrow the error for handling elsewhere if needed
  }
};

export const destinationListResponse = (
  resp: DistanceMatrixResponseType,
  destinations: Array<DestinationNodeType>
): NearByDestinationsInfoProps => {
  // creating inital object to push destinations under destinationList key
  const data: NearByDestinationsInfoProps = { destinationList: [] };
  for (let i = 0; i < destinations.length; i++) {
    data.destinationList.push({
      destination: buildLocationText(
        destinations[i].participatingProperties.city,
        destinations[i].participatingProperties.stateProvince,
        destinations[i].participatingProperties.country
      ),
      distance: resp.rows[0].elements[i].distance.text,
      duration: convertInHours(resp.rows[0].elements[i].duration.value),
      placeId: '',
      address: buildLocationText(
        destinations[i].participatingProperties.city,
        destinations[i].participatingProperties.country
      ),
      city: destinations[i].participatingProperties.city ?? '',
      country: destinations[i].participatingProperties.countryCode ?? '',
      latitude: `${destinations[i].participatingProperties.properties[0].basicInformation.latitude}`,
      longitude: `${destinations[i].participatingProperties.properties[0].basicInformation.longitude}`,
      state: destinations[i].participatingProperties.stateProvince,
      seconds: resp.rows[0].elements[i].distance.value,
      hoursAwayText: resp.rows[0].elements[i].duration.text,
    });
  }
  return { ...data, destinationList: data.destinationList.sort((a, b) => a.duration - b.duration) };
};

export const returnValidNumber = (num: string): number => {
  const number = Number(num);
  return isNaN(number) ? 0 : number;
};

export const convertInHours = (sec: number): number => {
  let hours = returnValidNumber(`${sec}`);
  hours = Math.round(sec / 3600);
  return hours;
};

export const constructOfferSearchURL = (baseURL: string, destinationData: DestinationDetails): string => {
  const queryParams = [
    ['destinationAddress.placeId', destinationData?.placeId],
    ['destinationAddress.latitude', destinationData?.latitude],
    ['isInternalSearch', 'true'],
    ['destinationAddress.country', destinationData?.country],
    ['vsInitialRequest', 'false'],
    ['searchType', 'InCity'],
    ['destinationAddress.address', destinationData?.address],
    ['destinationAddress.city', destinationData?.city],
    ['destinationAddress.mainText', destinationData?.city],
    ['destinationAddress.countryName', destinationData?.country],
    ['destinationAddress.longitude', destinationData?.longitude],
    ['destinationAddress.stateProvince', destinationData?.state],
    ['destinationAddress.initialRequest', 'false'],
    ['destinationAddress.singleSearchAutoSuggest', 'Unmatched'],
    ['destinationAddress.destination', destinationData?.destination],
  ];

  const serializedQueryParams = queryParams
    .filter(([, value]) => hasQueryParamValue(value))
    .map(([key, value]) => `${key}=${value}`)
    .join('&');

  return serializedQueryParams ? `${baseURL}?${serializedQueryParams}` : baseURL;
};

export const generateCardIndexArray = (length: number): string[] => {
  return Array.from({ length }, (_, index) => `card-${index}`);
};

export const getHoursAwayText = (
  duration: number,
  hourText: string | undefined,
  hoursText: string | undefined
): string => {
  return `${duration} ${duration > 1 ? hoursText : hourText}`;
};
